import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Slide,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  ViewWeek as WeekViewIcon,
  CalendarViewMonth as MonthViewIcon
} from '@mui/icons-material';

const API_URL = '/api';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const COLORES_ESTADO = {
  confirmado: { bg: '#dcfce7', color: '#16a34a' },
  cancelado: { bg: '#fee2e2', color: '#dc2626' },
  pendiente: { bg: '#fef3c7', color: '#d97706' }
};

// Componente InfoField
const InfoField = ({ label, value, color = "#1e3a5f" }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, fontSize: 11 }}>
    <Typography component="span" sx={{ fontWeight: 600, color: "#64748b", fontSize: 10 }}>
      {label}:
    </Typography>
    <Typography component="span" sx={{ color, fontWeight: 500, fontSize: 11 }}>
      {value || "—"}
    </Typography>
  </Box>
);

const AgendaCitas = ({ open, onClose, userId, onCitaSelected }) => {
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fechaActual, setFechaActual] = useState(new Date());
  const [estadisticas, setEstadisticas] = useState(null);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [vista, setVista] = useState('semana'); // 'semana' o 'mes'

  const cargarCitas = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    try {
      let fechaInicio, fechaFin;
      const fecha = new Date(fechaActual);

      if (vista === 'semana') {
        const diaSemana = fecha.getDay();
        fechaInicio = new Date(fecha);
        fechaInicio.setDate(fecha.getDate() - diaSemana);
        fechaInicio.setHours(0, 0, 0, 0);
        fechaFin = new Date(fechaInicio);
        fechaFin.setDate(fechaInicio.getDate() + 6);
        fechaFin.setHours(23, 59, 59, 999);
      } else {
        // Vista mensual
        fechaInicio = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
        fechaFin = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0, 23, 59, 59, 999);
      }

      const params = new URLSearchParams({
        fecha_inicio: fechaInicio.toISOString(),
        fecha_fin: fechaFin.toISOString(),
        medico_id: userId,  // Filtrar solo citas del médico actual
        limit: '200'
      });

      const [citasRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/emr/citas?${params}`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/emr/citas/estadisticas?medico_id=${userId}`, { headers: { 'X-User-Id': userId } })
      ]);

      if (citasRes.ok) setCitas((await citasRes.json()).citas || []);
      if (statsRes.ok) setEstadisticas(await statsRes.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, fechaActual, vista]);

  useEffect(() => {
    if (open) cargarCitas();
  }, [open, cargarCitas]);

  const navegarAnterior = () => {
    const nueva = new Date(fechaActual);
    if (vista === 'semana') {
      nueva.setDate(nueva.getDate() - 7);
    } else {
      nueva.setMonth(nueva.getMonth() - 1);
    }
    setFechaActual(nueva);
  };

  const navegarSiguiente = () => {
    const nueva = new Date(fechaActual);
    if (vista === 'semana') {
      nueva.setDate(nueva.getDate() + 7);
    } else {
      nueva.setMonth(nueva.getMonth() + 1);
    }
    setFechaActual(nueva);
  };

  // Obtener días de la semana
  const getDiasSemana = () => {
    const dias = [];
    const fecha = new Date(fechaActual);
    const diaSemana = fecha.getDay();
    const inicioSemana = new Date(fecha);
    inicioSemana.setDate(fecha.getDate() - diaSemana);
    for (let i = 0; i < 7; i++) {
      const dia = new Date(inicioSemana);
      dia.setDate(inicioSemana.getDate() + i);
      dias.push(dia);
    }
    return dias;
  };

  // Obtener días del mes (con días del mes anterior/siguiente para completar semanas)
  const getDiasMes = () => {
    const fecha = new Date(fechaActual);
    const año = fecha.getFullYear();
    const mes = fecha.getMonth();

    const primerDia = new Date(año, mes, 1);
    const ultimoDia = new Date(año, mes + 1, 0);

    const dias = [];

    // Días del mes anterior para completar la primera semana
    const primerDiaSemana = primerDia.getDay();
    for (let i = primerDiaSemana - 1; i >= 0; i--) {
      const dia = new Date(año, mes, -i);
      dias.push({ fecha: dia, esOtroMes: true });
    }

    // Días del mes actual
    for (let i = 1; i <= ultimoDia.getDate(); i++) {
      dias.push({ fecha: new Date(año, mes, i), esOtroMes: false });
    }

    // Días del mes siguiente para completar la última semana
    const diasRestantes = 42 - dias.length; // 6 semanas * 7 días
    for (let i = 1; i <= diasRestantes; i++) {
      dias.push({ fecha: new Date(año, mes + 1, i), esOtroMes: true });
    }

    return dias;
  };

  const getCitasPorDia = (fecha) => {
    return citas.filter(c => new Date(c.fecha_cita).toDateString() === fecha.toDateString())
      .sort((a, b) => new Date(a.fecha_cita) - new Date(b.fecha_cita));
  };

  const actualizarEstadoCita = async (citaId, nuevoEstado) => {
    try {
      const res = await fetch(`${API_URL}/emr/citas/${citaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      if (res.ok) {
        cargarCitas();
        setCitaSeleccionada(null);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const formatHora = (fecha) => new Date(fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  const diasSemana = getDiasSemana();
  const diasMes = getDiasMes();
  const hoy = new Date().toDateString();

  // Título según vista
  const getTitulo = () => {
    if (vista === 'semana') {
      return `${diasSemana[0].toLocaleDateString('es-MX', { month: 'short' })} ${diasSemana[0].getDate()} - ${diasSemana[6].getDate()}`;
    } else {
      return `${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          overflow: "hidden"
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 1.5,
          px: 2.5
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CalendarIcon sx={{ fontSize: 24, color: "#fbbf24" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Agenda de Citas</Typography>
          {estadisticas && (
            <Chip
              label={`${estadisticas.total_hoy} hoy`}
              size="small"
              sx={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, fontSize: 10, height: 20 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={cargarCitas} sx={{ color: '#fff', p: 0.5 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: '#fff', p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Navegación y controles */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", background: "#fff", display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton onClick={navegarAnterior} size="small" sx={{ p: 0.5 }}>
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Button
              size="small"
              startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => setFechaActual(new Date())}
              sx={{ fontSize: 11, px: 1, minWidth: 'auto' }}
            >
              Hoy
            </Button>
            <IconButton onClick={navegarSiguiente} size="small" sx={{ p: 0.5 }}>
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography sx={{ fontWeight: 600, color: '#1e3a5f', fontSize: 13, ml: 1 }}>
              {getTitulo()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Toggle de vista */}
            <ToggleButtonGroup
              value={vista}
              exclusive
              onChange={(e, v) => v && setVista(v)}
              size="small"
              sx={{ height: 28 }}
            >
              <ToggleButton value="semana" sx={{ px: 1.5, py: 0.5, fontSize: 11 }}>
                <WeekViewIcon sx={{ fontSize: 16, mr: 0.5 }} /> Semana
              </ToggleButton>
              <ToggleButton value="mes" sx={{ px: 1.5, py: 0.5, fontSize: 11 }}>
                <MonthViewIcon sx={{ fontSize: 16, mr: 0.5 }} /> Mes
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Estadísticas */}
            {estadisticas && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {[
                  { label: 'Pend', value: estadisticas.pendientes, color: '#d97706' },
                  { label: 'Conf', value: estadisticas.confirmadas, color: '#16a34a' },
                  { label: 'Canc', value: estadisticas.canceladas, color: '#dc2626' }
                ].map(s => (
                  <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color }} />
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>{s.value}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Calendario */}
        <Box sx={{ p: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : vista === 'semana' ? (
            /* Vista Semanal */
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {diasSemana.map((dia, idx) => {
                const citasDelDia = getCitasPorDia(dia);
                const esHoy = dia.toDateString() === hoy;

                return (
                  <Box
                    key={idx}
                    sx={{
                      borderRadius: 2,
                      bgcolor: esHoy ? '#eff6ff' : '#fff',
                      border: esHoy ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                      minHeight: 200,
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 1, textAlign: 'center', borderBottom: '1px solid #e2e8f0', bgcolor: esHoy ? '#3b82f6' : '#f8fafc' }}>
                      <Typography sx={{ fontSize: 10, color: esHoy ? '#fff' : '#64748b', fontWeight: 600 }}>
                        {DIAS_SEMANA[idx]}
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: esHoy ? '#fff' : '#1e3a5f' }}>
                        {dia.getDate()}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                      {citasDelDia.length === 0 ? (
                        <Typography sx={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', py: 2 }}>—</Typography>
                      ) : (
                        citasDelDia.map(cita => {
                          const estado = cita.estado || 'pendiente';
                          const colores = COLORES_ESTADO[estado];
                          return (
                            <Box
                              key={cita.id}
                              onClick={() => setCitaSeleccionada(cita)}
                              sx={{
                                p: 0.5,
                                mb: 0.5,
                                borderRadius: 1,
                                bgcolor: colores.bg,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                '&:hover': { transform: 'scale(1.02)', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }
                              }}
                            >
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: colores.color }}>
                                {formatHora(cita.fecha_cita)}
                              </Typography>
                              <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cita.paciente_nombre}
                              </Typography>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            /* Vista Mensual */
            <Box>
              {/* Encabezado días de la semana */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                {DIAS_SEMANA.map(dia => (
                  <Typography key={dia} sx={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: '#64748b', py: 0.5 }}>
                    {dia}
                  </Typography>
                ))}
              </Box>
              {/* Días del mes */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {diasMes.map(({ fecha, esOtroMes }, idx) => {
                  const citasDelDia = getCitasPorDia(fecha);
                  const esHoy = fecha.toDateString() === hoy;
                  const tieneCitas = citasDelDia.length > 0;

                  return (
                    <Box
                      key={idx}
                      onClick={() => tieneCitas && setCitaSeleccionada(citasDelDia[0])}
                      sx={{
                        p: 0.5,
                        minHeight: 50,
                        borderRadius: 1,
                        bgcolor: esHoy ? '#eff6ff' : esOtroMes ? '#f8fafc' : '#fff',
                        border: esHoy ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                        cursor: tieneCitas ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                        '&:hover': tieneCitas ? { bgcolor: '#f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' } : {}
                      }}
                    >
                      <Typography sx={{
                        fontSize: 11,
                        fontWeight: esHoy ? 700 : 500,
                        color: esOtroMes ? '#94a3b8' : esHoy ? '#3b82f6' : '#1e3a5f',
                        textAlign: 'center'
                      }}>
                        {fecha.getDate()}
                      </Typography>
                      {tieneCitas && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25, flexWrap: 'wrap' }}>
                          {citasDelDia.slice(0, 3).map(cita => {
                            const colores = COLORES_ESTADO[cita.estado || 'pendiente'];
                            return (
                              <Box
                                key={cita.id}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: colores.color
                                }}
                              />
                            );
                          })}
                          {citasDelDia.length > 3 && (
                            <Typography sx={{ fontSize: 8, color: '#64748b' }}>+{citasDelDia.length - 3}</Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Panel de detalle */}
        {citaSeleccionada && (
          <Box sx={{ mx: 1.5, mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: 14 }}>
                  {citaSeleccionada.paciente_nombre}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <InfoField label="Fecha" value={new Date(citaSeleccionada.fecha_cita).toLocaleDateString('es-MX')} />
                  <InfoField label="Hora" value={formatHora(citaSeleccionada.fecha_cita)} color="#6366f1" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <WhatsAppIcon sx={{ fontSize: 12, color: '#25d366' }} />
                  <Typography sx={{ fontSize: 11, color: '#475569' }}>{citaSeleccionada.numero_whatsapp}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={citaSeleccionada.estado || 'Pendiente'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    bgcolor: COLORES_ESTADO[citaSeleccionada.estado || 'pendiente'].bg,
                    color: COLORES_ESTADO[citaSeleccionada.estado || 'pendiente'].color,
                    fontWeight: 600
                  }}
                />
                <IconButton size="small" onClick={() => setCitaSeleccionada(null)} sx={{ p: 0.3 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>

            {(citaSeleccionada.estado !== 'confirmado' && citaSeleccionada.estado !== 'cancelado') && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                  onClick={() => actualizarEstadoCita(citaSeleccionada.id, 'confirmado')}
                  sx={{ flex: 1, fontSize: 11, py: 0.5, borderRadius: 1.5 }}
                >
                  Confirmar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                  onClick={() => actualizarEstadoCita(citaSeleccionada.id, 'cancelado')}
                  sx={{ flex: 1, fontSize: 11, py: 0.5, borderRadius: 1.5 }}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            fontSize: 13,
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgendaCitas;
